/**
 * Axの注文を作成するためのclass
 * $cart に依存
 * PH1 で修正しきれなかった為PH2内できれいにする必要あり
 */
import Vue from 'vue'
import { AxE2Cart, AxPaymentMethod } from './$cart'
import { $axFrontendHelper } from './$axFrontendHelper'
import {
  AxAvailableDeliveryMethods,
  AxCommitableOrderResponse,
  AxDeliverySelection,
  AxOrderBuilderDeliveryUnit,
  AxOrderBuilderRes,
  AxOrderBuilderCommitResponse,
} from '@/types/AxOrderTypes'
import { axApiPaths } from '@/AxApiPaths'
import { $axAuthState } from './$axAuthStateManager'
import { $toast } from './$toast'
import { $lsCache } from './$lsCache'
import { AxUserAddress } from '@/types/AxUserType'
import { PAYMENT_METHOD, PaymentMethodCd } from '@/types/carts'
import { signupColumns } from '@/models/signup'
import { CouponDetail } from '@/types/coupon'

type AxOrderCommitParam = {
  temporary_order_id: string // "c0fadb4e-caf8-474c-93db-c9bf585c4c1a",
  guest_purchaser: boolean
  payment_config?: AxOrderCommitParamPaymentConfig
}

type AxOrderCommitParamPaymentConfig = paymentConfigGMOPG | paymentConfigFreePayment
type paymentConfigGMOPG = {
  card_id?: string // "2",
  token?: string // "c0dedce4b17d10b5c48abdac933c2857bf7d19813907c469b6ab57a058b99ba3",
  method: '1' | '2' | '3' | '4' | '5' // 支払方法（1：一括、2：分割、3：ボーナス一括、4：ボーナス分割、5：リボ）一括固定,
  need_save_card: boolean
}

type paymentConfigFreePayment = {
  payment_status: 'unpaid' | 'captured' | 'authorized'
}

const orderCommiterLsCacheKey = {
  temporary_order_id: 'AxE2--cart-temporary_order_id',
  custom: 'AxE2--cart-custom-data',
  placement_first: 'AxE2--cart-placement-first',
  placement_second: 'AxE2--cart-placement-second',
  call_flag: 'AxE2--cart-call-flag',
  remarks: 'AxE2--cart-remarks',
}

export class AxOrderCommitter {
  $cart: AxE2Cart
  // temporary_order_id: string
  delivery_units: AxOrderBuilderDeliveryUnit[]
  comittableOrderDetail: AxCommitableOrderResponse
  // _temporary_order_id: string
  _usedCoupon: {
    detail: CouponDetail
    secret: string
  }
  _orderCustomData: { [keyName: string]: any }
  _placement_first: string
  _placement_second: string
  _call_flag: boolean
  _remarks: string
  deliveryFee: number

  constructor($cart: AxE2Cart) {
    this.$cart = $cart
    // this.temporary_order_id = ''
    this.delivery_units = []
    this.comittableOrderDetail = null
    this.deliveryFee = 0
  }

  /**
   * 現在のカート情報を元に仮注文作成
   */
  async create() {
    // 仮注文を作成
    const create = await this._create()
    if (!create || create.warnings?.length > 0) {
      // TODO エラーハンドリング入れる
      return false
    } else {
      // 必要情報を保存
      this.temporary_order_id = create.result.temporary_order_id
      Vue.set(this, 'delivery_units', create.result.delivery_units)
    }

    // カート内の商品を仮注文へロード
    const cartLoad = await this._cartLoad(this.temporary_order_id)
    if (!cartLoad || cartLoad.warnings.length > 0) {
      // TODO エラーハンドリング入れる
      return false
    } else {
      return true
    }
  }

  /**
   * 現在のカート情報を元に仮注文作成
   */
  async cartLoad() {
    if (!this.temporary_order_id) {
      return false
    }
    const result = await this._cartLoad(this.temporary_order_id)
    if (!result || result.warnings?.length > 0) {
      // TODO エラーハンドリング入れる
      return false
    } else {
      return true
    }
  }

  /**
   * 現在のカート情報を元に仮注文作成
   */
  async get(): Promise<AxCommitableOrderResponse> {
    if (!this.temporary_order_id) {
      return null
    }
    const result = await this._get(this.temporary_order_id)
    if (!result || result.warnings?.length > 0) {
      // TODO エラーハンドリング入れる
      return null
    } else {
      return result.result
    }
  }
  async verify(type?) {
    if (!this.temporary_order_id) {
      return false
    }
    const result = await this._verify(this.temporary_order_id)
    if (!result || result.warnings?.length > 0) {
      if (type === 'payment' && result.warnings.some(({ code }) => code === '023-017')) {
        return true
      }
      // TODO エラーハンドリング入れる
      if ((type = 'cart')) {
        return true
      }
      return false
    } else {
      return true
    }
  }

  /**
   * お届け先getter/setter
   */
  set temporary_order_id(str: string) {
    $lsCache.set(orderCommiterLsCacheKey.temporary_order_id, str)
  }
  get temporary_order_id(): string {
    return $lsCache.get(orderCommiterLsCacheKey.temporary_order_id)
  }

  /**
   * 配送方法を取得
   * @param unitNo 配送区分
   * @returns 配送方法の候補
   */
  async getDelivery(unitNo): Promise<AxDeliverySelection> {
    // TODO:配送指定日、指定日時を独自実装する必要があるかも
    const res: AxAvailableDeliveryMethods = await this._getDeliveryMethods(unitNo)
    if (res.delivery_method_selections?.length < 1) {
      $toast.error('カートの状態が正しくありません。')
      return null
    }
    if (this.$cart._cartItemsReactive.deliveryType == 0) {
      return res.delivery_method_selections.find(section => section.id == '100')
    } else {
      return res.delivery_method_selections.find(section => section.id == '101')
    }
  }

  /**
   * orderCustomData: Record<string, any>
   */
  set orderCustomData(customData: { [keyName: string]: any }) {
    // remove invalid key
    Object.keys(customData).map((key) => {
      if (!customData[key] && customData[key] !== 0) {
        delete customData[key]
      }
    })
    $lsCache.set(orderCommiterLsCacheKey.custom, customData || {})
    Vue.set(this, '_orderCustomData', customData)
  }
  get orderCustomData(): { [keyName: string]: any } {
    const customData = $lsCache.get(orderCommiterLsCacheKey.custom)
    Vue.set(this, '_orderCustomData', customData)
    return this._orderCustomData
  }

  /**
   *置き配第一希望 getter/setter
   */
  set placementFirst(str: string) {
    $lsCache.set(orderCommiterLsCacheKey.placement_first, str)
    this._placement_first = str
  }
  get placementFirst(): string {
    const str = $lsCache.get(orderCommiterLsCacheKey.placement_first)
    this._placement_first = str
    return this._placement_first
  }

  /**
   *置き配第二希望 getter/setter
   */
  set placementSecond(str: string) {
    $lsCache.set(orderCommiterLsCacheKey.placement_second, str)
    this._placement_second = str
  }
  get placementSecond(): string {
    const str = $lsCache.get(orderCommiterLsCacheKey.placement_second)
    this._placement_second = str
    return this._placement_second
  }

  /**
   * 呼び鈴 getter/setter
   */
  set callFlag(flg: boolean) {
    $lsCache.set(orderCommiterLsCacheKey.call_flag, flg)
    this._call_flag = flg
  }
  get callFlag(): boolean {
    const flg = $lsCache.get(orderCommiterLsCacheKey.call_flag)
    this._call_flag = flg
    return this._call_flag
  }

  /**
   * 備考 getter/setter
   */
  set remarks(str: string) {
    $lsCache.set(orderCommiterLsCacheKey.remarks, str)
    this._remarks = str
  }
  get remarks(): string {
    const str = $lsCache.get(orderCommiterLsCacheKey.remarks)
    this._remarks = str
    return str
  }
  /**
   *
   * @returns
   */
  async _create() {
    try {
      const res = await $axFrontendHelper.request({
        path: axApiPaths.orders.create,
        data: {
          query: {
            cart_codes: [this.$cart.cart_code],
          },
        },
      })
      console.log('[OrderCommitter._create] res: ', res)
      // if (hooks) {
      //   await $appHook.callHooks($AxE2AppHooks.cart.add.AFTER, lines, this)
      // }
      return res
    } catch (e) {
      // await $appHook.callHooks($AxE2AppHooks.cart.add.ERROR, lines, this)
      $toast.error('カート作成に失敗しました')
      return null
    }
  }

  /**
   * カートを仮注文へ
   * @param orderId
   * @returns
   */
  async _cartLoad(orderId) {
    try {
      const res = await $axFrontendHelper.request({
        path: axApiPaths.orders.load,
        data: {
          query: {
            temporary_order_id: orderId,
            cart_codes: [this.$cart.cart_code],
          },
        },
      })
      // if ('\"code\":\"010-007'
      // if (hooks) {
      //   await $appHook.callHooks($AxE2AppHooks.cart.add.AFTER, lines, this)
      // }
      console.log('[OrderCommitter._cartLoad] res: ', res)
      return res
    } catch (e) {
      // await $appHook.callHooks($AxE2AppHooks.cart.add.ERROR, lines, this)
      return null
    }
  }

  /**
   * 仮注文情報取得
   * @param orderId
   * @returns
   */
  async _get(orderId) {
    try {
      const res = await $axFrontendHelper.request({
        path: axApiPaths.orders.builder.get,
        data: {
          query: {
            temporary_order_id: orderId,
          },
        },
      })
      // if (hooks) {
      //   await $appHook.callHooks($AxE2AppHooks.cart.add.AFTER, lines, this)
      // }
      Vue.set(this, 'comittableOrderDetail', res.result)
      // console.log(`comittableOrderDetail:`)
      // console.log(res.result)
      return res
    } catch (e) {
      // await $appHook.callHooks($AxE2AppHooks.cart.add.ERROR, lines, this)
      $toast.error('カート情報の取得に失敗しました')
      return null
    }
  }

  /**
   *
   * @param orderId
   * @returns
   */
  async _verify(orderId) {
    try {
      const res = await $axFrontendHelper.request({
        path: axApiPaths.orders.verify,
        data: {
          query: {
            temporary_order_id: orderId,
          },
        },
      })
      // if (hooks) {
      //   await $appHook.callHooks($AxE2AppHooks.cart.add.AFTER, lines, this)
      // }
      // Vue.set(this, 'comittableOrderDetail', res.result)
      await this._get(orderId)
      Vue.set(this, 'delivery_units', res.result.delivery_units as AxOrderBuilderDeliveryUnit[])
      return res
    } catch (e) {
      // console.log(e.message)
      // await $appHook.callHooks($AxE2AppHooks.cart.add.ERROR, lines, this)
      return null
    }
  }

  /**
   *
   * @param query
   * @returns
   */
  async _set(query) {
    try {
      const res = await $axFrontendHelper.request({
        path: axApiPaths.orders.set,
        data: {
          query,
        },
      })
      // if (hooks) {
      //   await $appHook.callHooks($AxE2AppHooks.XXX.XXX.AFTER, lines, this)
      // }
      console.log('[_set]res: ', res)
      return res
    } catch (e) {
      // await $appHook.callHooks($AxE2AppHooks.XXX.XXX.ERROR, lines, this)
      $toast.error('お届け先を設定できませんでした。')
      return null
    }
  }

  /**
   * 仮注文配送方法候補取得
   *   配送区分ごとに適用される選択可能な配送方法を取得する
   * @param delivery_unit_no
   * @private
   */
  async _getDeliveryMethods(delivery_unit_no: number): Promise<AxAvailableDeliveryMethods> {
    try {
      const res = await $axFrontendHelper.request<AxAvailableDeliveryMethods>({
        path: axApiPaths.orders.delivery.get,
        data: {
          query: {
            temporary_order_id: this.temporary_order_id,
            delivery_unit_no: delivery_unit_no,
          },
        },
      })
      // if (hooks) {
      //   await $appHook.callHooks($AxE2AppHooks.XXX.XXX.AFTER, lines, this)
      // }
      return res.result
    } catch (e) {
      // await $appHook.callHooks($AxE2AppHooks.XXX.XXX.ERROR, lines, this)
      $toast.error('配送時間帯情報を取得できませんでした。')
      return null
    }
  }

  /**
   * Ax上でカートを生成する
   * @private
   */
  async createPreOrder() {
    // カートへ商品をセット
    await this._setCartItemsWithCurrentCartItems()
    const orderCreateResponse = await $axFrontendHelper.request<AxOrderBuilderRes>({
      path: axApiPaths.orders.create,
    })
    this.temporary_order_id = orderCreateResponse.result.temporary_order_id
    this.delivery_units = orderCreateResponse.result.delivery_units
  }

  /**
   * 現在のカートの内容で、Axのカートを作成する
   * @private
   */
  async _setCartItemsWithCurrentCartItems() {
    // 一度カートの内容をクリアしなければ...
    const res1 = await $axFrontendHelper.request({
      path: axApiPaths.carts.clear,
      data: {
        cart: {
          cart_code: this.$cart.cart_code,
        },
      },
    })
    // Axのカートへアイテムをセット
    const res2 = await $axFrontendHelper.request({
      path: axApiPaths.carts.put,
      data: {
        query: {
          cart: {
            cart_code: this.$cart.cart_code,
            lines: Object.values(this.$cart._cartItemsReactive),
          },
        },
      },
    })
  }

  _getCustomForInformationSetParam() {
    const custom = this.orderCustomData
    if (!custom || !Object.keys(custom).length) {
      return {}
    }
    const customFields = Object.keys(custom).reduce((res, customFieldName) => {
      const val = custom[customFieldName]
      if (val || val === 0) {
        res[customFieldName] = val.id ? val.id : val
      }
      return res
    }, {})
    return customFields
  }
  /**
   * 配送先住所をセット
   * @private
   */
  async _setDeliveryAddress(signUpData: typeof signupColumns) {
    if (!this.$cart.deliveryAddress) {
      $toast.error('お届け先が設定されていません')
      return false
    }
    // 仮注文情報登録
    const result = await this.updateOrderInfo(signUpData)
    if (!result) {
      // TODO エラーハンドリング入れる
      return false
    } else if (result.warnings?.length > 0) {
      // TODO エラーハンドリング入れる
      return true
    } else {
      return true
    }
  }

  async getOrderInfo(signUpData: typeof signupColumns = null) {
    const deliveryAddress = this.$cart.deliveryAddress
    const userProfile = await $axAuthState.getCurrentLoggedInUserProfile()
    let purchaser = {}
    if ($axAuthState.isLoggedIn) {
      purchaser = {
        name: userProfile.name,
        name_read: userProfile.name_read,
        email: userProfile.email,
        tel: userProfile.tel_primary,
        zip: userProfile.zip,
        state: userProfile.state,
        city: userProfile.city,
        street: userProfile.street,
        building: userProfile.building,
      }
    } else {
      purchaser = {
        name: signUpData.custom.family_name + ' ' + signUpData.custom.reputation,
        name_read: signUpData.custom.family_name_kana + ' ' + signUpData.custom.reputation_kana,
        email: signUpData.email,
        tel: signUpData.tel_number1 + signUpData.tel_number2 + signUpData.tel_number3,
        zip: signUpData.zip,
        state: signUpData.state,
        city: signUpData.city,
        street: signUpData.street,
        building: signUpData.building,
      }
    }

    // パラメータ定義
    const query: { temporary_order_id; purchaser; reciever; custom? } = {
      temporary_order_id: this.temporary_order_id,
      purchaser: $axFrontendHelper.cleanupObjectForRequest(purchaser),
      reciever: convertUserDeliveryAddressToOrderDeliveryAddress(
        $axFrontendHelper.cleanupObjectForRequest(deliveryAddress)
      ),
    }
    // カスタムカラム取得
    const custom = this._getCustomForInformationSetParam()

    // カスタムに定期コース区分を追加
    if (this.$cart.course_cd) {
      custom['course_cd'] = this.$cart.course_cd
    }

    if (custom && Object.keys(custom).length > 0) {
      query.custom = custom
    }

    return query
  }

  /**
   * 仮注文情報をまとめて登録
   * （全情報を一度に入れないといけないため）
   */
  async updateOrderInfo(signUpData: typeof signupColumns = null) {
    const query = await this.getOrderInfo(signUpData)

    return this._set(query)
  }

  /**
   * 配送区分単位で、配送方法をセット
   * セットする際に、日付, 時間帯指定も付与することが可能
   * @param delivery_unit_no
   * @param delivery_method
   * @private
   */
  async _setDeliveryMethods(
    delivery_unit_no: number,
    {
      id,
      delivery_date = null,
      delivery_time = null,
      delivery_fee = null,
      force_delivery_fee = false,
    }: {
      id
      delivery_date?
      delivery_time?
      delivery_fee?
      force_delivery_fee?
    }
  ): Promise<AxDeliverySelection> {
    const delivery_method: any = { id: `${id}` }
    if (delivery_date) {
      delivery_method.delivery_date = delivery_date
    }
    if (delivery_time) {
      delivery_method.delivery_time = delivery_time
    }
    if (delivery_fee) {
      delivery_method.delivery_fee = delivery_fee
    }
    delivery_method.force_delivery_fee = force_delivery_fee
    try {
      const res = await $axFrontendHelper.request<{ delivery_method: AxDeliverySelection }>({
        path: axApiPaths.orders.delivery.set,
        data: {
          query: {
            temporary_order_id: this.temporary_order_id,
            delivery_unit_no,
            delivery_method,
          },
        },
      })
      console.log('[AxOrderCommitter._setDeliveryMethod] res: ', res)
      return res.result.delivery_method
    } catch (e) {
      console.log('[AxOrderCommitter._setDeliveryMethod] e.message: ', e.message)
      $toast.error('選択できない日時です')
      console.groupEnd()
      return null
    }
  }

  /**
   * 選択可能な支払方法を取得
   */
  async getPaymentMethods(
    totalPrice: number,
    trial_flg: boolean = false
  ): Promise<{
    payment_profile_selections: AxPaymentMethod[]
    payment_profile_selected: AxPaymentMethod
  }> {
    try {
      const res = await $axFrontendHelper.requestToNormal({
        params: { axApiPath: axApiPaths.orders.payment.get },
        path: axApiPaths.orders.payment.get,
        method: 'POST',
        data: {
          total_price: totalPrice,
          temporary_order_id: this.temporary_order_id,
          trial_flg: trial_flg,
        },
      })
      return res
    } catch (e) {
      $toast.error('選択可能なお支払い方法の取得に失敗しました')
      return null
    }
  }

  /**
   * お支払い方法を設定
   * @private
   */
  async _setPaymentInfo(totalPrice: number) {
    console.log('[_setPaymentInfo]paymentMethod: ', this.$cart.paymentMethod)
    try {
      const res = await $axFrontendHelper.request({
        path: axApiPaths.orders.payment.set,
        data: {
          query: {
            temporary_order_id: this.temporary_order_id,
            payment_profile: this.$cart.paymentMethod || { id: '108' },
          },
        },
      })
      return res.result
    } catch (e) {
      $toast.error('選択可能なお支払い方法の設定に失敗しました')
      return null
    }
  }

  /**
   * 不正検知
   */
  async oplux(deviceInfo: string) {
    try {
      const res = await $axFrontendHelper.requestToNormal({
        params: { axApiPath: axApiPaths.orders.oplux },
        path: axApiPaths.orders.oplux,
        method: 'POST',
        data: {
          device_info: deviceInfo,
          temporary_order_id: this.temporary_order_id,
        },
      })
      console.log('不正検知実行結果：' + res)
      if (res === 'NG') {
        $toast.error(
          'ご注文内容について確認事項があるため、注文を承ることができません。\n' +
            'ご注文を希望される場合は、お手数ですが、お客様センターまでご連絡ください。\n' +
            '0120-854-014　平日9:00-17:30（土日祝、年末年始を除く）'
        )
        return ''
      }
      return res
    } catch (e) {
      $toast.error('不正検知ツール実行に失敗しました。')
      return ''
    }
  }

  get isCreditCardPayment() {
    // 支払い方法がクレジットカード
    return this.comittableOrderDetail.payment_profile.code === PaymentMethodCd[PAYMENT_METHOD.CreditCard]
  }

  /**
   * 注文を確定する
   * @returns
   */
  async commitOrder() {
    const commitParam: AxOrderCommitParam = {
      temporary_order_id: this.temporary_order_id,
      guest_purchaser: false,
    }

    // payment_configの作成
    if (this.comittableOrderDetail.payment_profile.code === PaymentMethodCd[PAYMENT_METHOD.CreditCard]) {
      // クレジットカードの場合
      commitParam.payment_config = {
        payment_status: 'authorized',
      }
    } else if (this.comittableOrderDetail.payment_profile.code === PaymentMethodCd[PAYMENT_METHOD.DirectDebit]) {
      // 自由決済（口座振替）の場合
      commitParam.payment_config = {
        payment_status: 'authorized',
      }
    }

    // 仮注文確定
    try {
      const res = await $axFrontendHelper.requestToNormal({
        params: { axApiPath: axApiPaths.orders.commit },
        path: axApiPaths.orders.commit,
        method: 'POST',
        data: {
          cart_code: this.$cart.cart_code,
          commitParam: commitParam,
        },
      })

      // カートをクリア
      this._cleanupCurrentState()
      return res
    } catch (e) {
      if (this.comittableOrderDetail.payment_profile.code === PaymentMethodCd[PAYMENT_METHOD.NP]) {
        $toast.error(
          'NP後払いはご利用いただけません。\n' + 'コールセンターまでお問い合わせください。\n' + '0120-854-014'
        )
      } else {
        $toast.error('ご注文を作成できませんでした。')
      }
      throw e
    }
  }

  _cleanupCurrentState() {
    this.$cart.cleanupState()
    orderCommiterLsCacheKey
    this.comittableOrderDetail = null
    this._cleanupOrderCaches()
  }

  _cleanupOrderCaches() {
    Object.keys(orderCommiterLsCacheKey).forEach((key) => {
      $lsCache.clear(orderCommiterLsCacheKey[key])
    })
  }
}

/**
 * Axのユーザ住所をお届け先住所に変換する
 * tel_primary => tel という罠がある等、同じオブジェクトの形式ではない
 * @param userDeliveryAddress
 */
export const convertUserDeliveryAddressToOrderDeliveryAddress = (userDeliveryAddress: AxUserAddress | any) => {
  // @ts-ignore
  userDeliveryAddress.tel = userDeliveryAddress.tel_primary
  return userDeliveryAddress
}
